import { useInfiniteQuery, useQuery } from "react-query";
import API from "./request";
import { API_PATH, QUERY_KEYS } from "../utils/config";
import { getCurrentPlan, getTotalStorage, getUserProfileById } from "./api";
import { normaliseUsage } from "../utils/storage";
import { shouldRetryQuery } from "./retryPolicy";

let default_config = {
  refetchOnWindowFocus: false,
  enabled: true,
};

// measure-web-mobile-sync-delay: how often a mounted Gallery grid polls for
// cross-client edits while the tab stays continuously focused (see
// useInfiniteWheels's `refetchIntervalMs`). Same order of magnitude as the
// existing LIVE_BADGE_POLL_MS convention (Chat.helpers.js, 30s).
export const GALLERY_LIVE_SYNC_POLL_MS = 15000;

export const useGetWheelHomeView = (params, config) => {
  return useQuery(
    [QUERY_KEYS.getWheelHomeView, params],
    () => API.get(API_PATH.GET_WHEEL_HOME_VIEW, { params }),
    { ...default_config, ...config }
  );
};

export const useGetLatestWheelId = (config = {}) => {
  const { enabled = false, ...restConfig } = config; // Default to disabled

  return useQuery(
    [QUERY_KEYS.getLatestWheel],
    () => API.get(API_PATH.GET_LATEST_AI_WHEEL),
    { ...default_config, enabled, ...restConfig }
  );
};

export const useGetAllCategories = (config) => {
  return useQuery(
    [QUERY_KEYS.getAllCategories],
    () => API.get(API_PATH.GET_ALL_CATEGORIES),
    { ...default_config, ...config }
  );
};

export const useGetAllTags = (config) => {
  return useQuery(
    [QUERY_KEYS.useGetAllTags],
    () => API.get(API_PATH.GET_ALL_TAGS),
    {
      ...default_config,
      ...config,
    }
  );
};

export const useSearchWheel = (params, config) => {
  return useQuery(
    [QUERY_KEYS.useSearchWheel, params],
    () => API.get(API_PATH.WHEEL_SEARCH, { params }),
    { ...default_config, ...config }
  );
};

export const useGetWheelSection = (wheel_id, config) => {
  return useQuery(
    [QUERY_KEYS.useGetWheelSection, wheel_id],
    () => API.get(API_PATH.GET_WHEEL_SECTION(wheel_id)),
    { ...default_config, ...config }
  );
};

export const useGetUserWheelsById = (user_id, params, config) => {
  console.log("useGetUserWheelsById called::");
  return useQuery(
    [QUERY_KEYS.useGetUserWheelsById, user_id],
    () => API.get(API_PATH.GET_USER_WHEELS_BY_ID(user_id), { params }),
    { ...default_config, ...config }
  );
};

export const useGetMediaBrowserData = (obj, params, config) => {
  return useQuery(
    [QUERY_KEYS.useGetMediaBrowser, obj],
    () =>
      API.get(
        API_PATH.GET_MEDIA_BROWSER_DATA(
          obj.text,
          obj.type,
          obj.source,
          obj.page
        ),
        { params }
      ),
    { ...default_config, ...config }
  );
};

export const useGetUserWheelsById_V2 = (user_id, params, config) => {
  console.log("useGetUserWheelsById called::");
  return useQuery(
    [QUERY_KEYS.useGetUserWheelsById, user_id],
    () => API.get(API_PATH.GET_USER_WHEELS_BY_ID_V2(user_id), { params }),
    { ...default_config, ...config }
  );
};

export const useGetUserPrivateWheelsById = (user_id, params, config) => {
  console.log("useGetUserPrivateWheelsById called::");
  return useQuery(
    [QUERY_KEYS.useGetUserPrivateWheelsById, user_id],
    () => API.get(API_PATH.GET_USER_PRIVATE_WHEELS_BY_ID(user_id), { params }),
    { ...default_config, ...config }
  );
};

export const useGetUserUnlockedWheelsById = (user_id, params, config) => {
  console.log("useGetUserUnlockedWheelsById called::");
  return useQuery(
    [QUERY_KEYS.useGetUserUnlockedWheelsById, user_id],
    () => API.get(API_PATH.GET_USER_UNLOCKED_WHEELS_BY_ID(user_id), { params }),
    { ...default_config, ...config }
  );
};

export const useGetUserClonedWheelsById = (user_id, params, config) => {
  console.log("useGetUserClonedWheelsById called::");
  return useQuery(
    [QUERY_KEYS.useGetUserClonedWheelsById, user_id],
    () => API.get(API_PATH.GET_USER_CLONED_WHEELS_BY_ID(user_id), { params }),
    { ...default_config, ...config }
  );
};

export const useGetUserClonedSegmentssById = (user_id, params, config) => {
  console.log("useGetUserClonedSegmentssById called::");
  return useQuery(
    [QUERY_KEYS.useGetUserClonedSegmentssById, user_id],
    () => API.get(API_PATH.GET_USER_CLONED_SEGMENTS_BY_ID, { params }),
    { ...default_config, ...config }
  );
};

export const useGetPrimaryBusinessBox = (user_id, config) => {
  return useQuery(
    [QUERY_KEYS.useGetPrimaryBusinessBox, user_id],
    () => API.get(API_PATH.GET_PRIMARY_BUSINESSBOX(user_id)),
    { ...default_config, ...config }
  );
};

export const useGetUserProfileById = (user_id, config = {}) => {
  const { enabled: enabledOverride, ...restConfig } = config;
  return useQuery(
    [QUERY_KEYS.useGetUserProfileById, user_id],
    // fix-fe-5178-own-profile-fetch-still-2-3x-followup — call the shared
    // getUserProfileById (services/api.js) instead of hitting API.get
    // directly, so this query's request coalesces with redux's
    // getUserProfileByIdAction when both fire for the same user_id around
    // the same time (e.g. Gallery/index.jsx's own-profile mount effect vs.
    // this hook's own `profileData` query) instead of racing an independent
    // network call past react-query's own per-hook cache.
    () => getUserProfileById(user_id),
    // Guard: never fire with a falsy id (e.g. before wheelData resolves) — it
    // hit GET /api/v1/user/undefined/profile/ -> 404 on every load. Preserve any
    // caller-supplied `enabled` (Gallery) by AND-combining it with the id guard.
    { ...default_config, ...restConfig, enabled: (enabledOverride ?? true) && !!user_id }
  );
};

export const useGetWheel = (wheel_id, config = {}) => {
  const { emailKey = null, anonymousKey = null, ...queryConfig } = config;

  const params = {};
  if (emailKey) params.email_key = emailKey;
  if (anonymousKey) params.anonymous_key = anonymousKey;

  return useQuery(
    [QUERY_KEYS.useGetWheelSection, wheel_id, emailKey, anonymousKey],
    () =>
      wheel_id
        ? API.get(API_PATH.GET_WHEEL(wheel_id), { params })
        : new Promise((resolve, reject) => reject("No wheel_id provided")),
    {
      ...default_config,
      ...queryConfig,
      staleTime: 0, // Data is immediately considered stale
      cacheTime: 0, // Minimal or no caching
      enabled: !!wheel_id,
    }
  );
};

export const useCreateSetupIntent = (config) => {
  return useQuery(
    [QUERY_KEYS.useCreateSetupIntent],
    () => API.post(API_PATH.CREATE_SETUP_INTENT),
    { ...default_config, ...config }
  );
};

export const useGetAllSubcriptions = (
  package_duration,
  fromReferrals = false,
  config
) => {
  return useQuery(
    [QUERY_KEYS.useGetAllSubcriptions, package_duration],
    () =>
      API.get(
        `${API_PATH.GET_ALL_SUBSCRIPTION}?package_duration=${package_duration}&from_referral=${fromReferrals}`
      ),
    { ...default_config, ...config }
  );
};

/**
 * v2 storage usage (backend #1121). Unwraps the envelope and normalises the
 * payload via `normaliseUsage`, so consumers get a null-safe, byte-aware
 * view-model regardless of whether counting/backfill is on yet (zeros) or
 * whether the API sends raw bytes vs pre-formatted strings.
 */
export const useStorageUsage = (config = {}) => {
  return useQuery(
    [QUERY_KEYS.useStorageUsage],
    () => API.get(API_PATH.GET_STORAGE_USAGE()),
    {
      ...default_config,
      staleTime: 60 * 1000, // usage doesn't change second-to-second
      // Response envelopes in this codebase vary: { data: {...} } or { data: { data: {...} } }.
      select: (response) => {
        const body = response?.data;
        const payload = body?.data ?? body ?? {};
        return normaliseUsage(payload);
      },
      ...config,
    }
  );
};

export const useGetFAQ = (config) => {
  return useQuery([QUERY_KEYS.useGetFAQ], () => API.get(API_PATH.GET_FAQ), {
    ...default_config,
    ...config,
  });
};

export const useGetSubcriptionDetails = (subscription_id, config) => {
  return useQuery(
    [QUERY_KEYS.useGetSubscriptionDetails],
    () => API.get(API_PATH.GET_SUBSCRIPTION_DETAILS(subscription_id)),
    { ...default_config, ...config }
  );
};

export const useGetAllWallpapers = (config) => {
  return useQuery(
    [QUERY_KEYS.useGetAllWallpapers],
    () => API.get(API_PATH.GET_ALL_WALLPAPERS),
    { ...default_config, ...config }
  );
};

export const useGetWheelAuthors = (wheel_id, config) => {
  return useQuery(
    [QUERY_KEYS.useGetWheelDetails, wheel_id],
    () => API.get(API_PATH.GET_WHEEL_AUTHORS(wheel_id)),
    { ...default_config, ...config }
  );
};

// export const useGetPrimaryWheelV2 = () => {
//   return useQuery(
//     [QUERY_KEYS.useGetPrimaryWheel],
//     () => API.get(API_PATH.GET_PRIMARY_WHEEL_V2),
//     { ...default_config }
//   );
// };

// export const useGetUserFolders = () => {
//   return useQuery(
//     [QUERY_KEYS.useGetUserFolders],
//     () => API.get(API_PATH.GET_FOLDERS),
//     { ...default_config }
//   );
// };

export const useGetUserFolders = (
  { userId, parentId = null, search = "", recursive = false },
  options = {}
) => {
  return useInfiniteQuery(
    ["folders", userId, parentId, search, recursive],
    async ({ pageParam = 1 }) => {
      const params = new URLSearchParams();
      if (userId) params.append("user", userId);
      if (parentId) params.append("parent", parentId);
      if (pageParam) params.append("page", pageParam);
      if (search) params.append("search", search);
      // ISP-9999 (hide-contents-nonowner-folders-count0): BE opt-in flag
      // that widens `?parent=` past direct children to every descendant
      // folder id -- a guest's flattened collection view has no drill-down
      // UI for a nested sub-collection, so it needs that sub-collection's
      // wheels surfaced here instead.
      if (recursive) params.append("recursive", "1");
      params.append("page_size", 20);

      const response = await API.get(
        `${API_PATH.GET_FOLDERS}?${params.toString()}`
      );
      console.log({ response });
      return {
        results: response?.data?.results || [],
        pageInfo: {
          current_page: response?.data?.current_page || pageParam,
          total_pages: response?.data?.total_pages || 1,
          count: response?.data?.count || 0,
          next: response?.data?.next,
          previous: response?.data?.previous,
        },
      };
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage?.pageInfo) return undefined;
        return lastPage.pageInfo.current_page < lastPage.pageInfo.total_pages
          ? lastPage.pageInfo.current_page + 1
          : undefined;
      },
      keepPreviousData: false,
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery,
      ...options, // Merge any additional options passed from the caller
    }
  );
};

export const useGetPrimaryWheelV2 = (id) => {
  return useQuery(
    [QUERY_KEYS.useGetPrimaryWheel, id], // Unique query key
    async () => {
      const response = await API.get(API_PATH.GET_PRIMARY_WHEEL_V2(id));
      const data = response?.data?.data;

      // Modify and sort the response here
      if (data && data.wheel_segments) {
        data.wheel_segments = data.wheel_segments.sort(
          (a, b) => a.position - b.position
        );
      }

      return data; // Return the modified response
    },
    {
      enabled: !!id, // Ensure the query runs only if `id` exists
      staleTime: 0, // Always consider data stale
      cacheTime: 0, // No caching
      refetchOnMount: true, // Always refetch when component mounts
      refetchOnWindowFocus: true, // Refetch when window regains focus
      onError: (error) => {
        console.error("Failed to fetch primary wheel:", error);
      },
    }
  );
};

export const useInfiniteWheels = ({
  selectedTab,
  selectedCategory,
  searchedText,
  userId,
  selectedFilters = [],
  // Gallery sort view (gallery-arrangement). "custom"/undefined = the owner's
  // persisted drag order (send NO sort param); any other value maps to a backend
  // gallery-list sort param (<value>=true), mirroring the Explore endpoint.
  selectedSort = "custom",
  // measure-web-mobile-sync-delay: staleTime is already 0 and
  // refetchOnWindowFocus is already true (both react-query library defaults,
  // see useInfiniteWheels.queryConfig.test.js) — MEASURED live on
  // web-dev.iyuga.net that neither helps a gallery tab that stays
  // continuously focused: an edit made via another client (API PATCH,
  // simulating mobile) never appeared in 120s of passive waiting, only after
  // a window blur->focus cycle (~12s) or a manual reload. There is no
  // socket/push invalidation for ordinary wheel edits (NotificationHandler.js
  // only invalidates ["fetchWheels"] for AI-wheel-generation pushes).
  // `refetchIntervalMs` closes that gap with plain polling while the query is
  // mounted; omitted (undefined/false) it's a no-op, so existing non-gallery
  // callers (SegmentLibrary, CommandPalette) are unaffected.
  refetchIntervalMs = false,
}) => {
  const apiUrlMap = {
    Created: `/api/v2/wheel/?mode=created&user_id=${userId}&add_primary=true`,
    Cloned: `/api/v2/wheel/?is_clone=true&user_id=${userId}&add_primary=true`,
    Unlocked: `/api/v2/wheel/?mode=unlock&user_id=${userId}&add_primary=true`,
    Favourited: `/api/v2/wheel/?mode=favorite&user_id=${userId}&add_primary=true`,
  };

  const apiUrl = apiUrlMap[selectedCategory];
  const searchQuery = searchedText ? `&search=${searchedText}` : "";

  const filters = selectedFilters.length
    ? selectedFilters.map((filter) => `${filter}=true`).join("&")
    : "";
  const sortQuery =
    selectedSort && selectedSort !== "custom" ? `&${selectedSort}=true` : "";
  const fullApiUrl = `${apiUrl}${searchQuery}${
    filters ? `&${filters}` : ""
  }${sortQuery}`;

  return useInfiniteQuery(
    [
      "fetchWheels",
      // ISP-9999 (collab-explore-002): `selectedTab` is a caller label only —
      // it's never read below (the request is fully determined by
      // selectedCategory/searchedText/selectedFilters/userId/selectedSort) —
      // so it must NOT be in the cache key. Including it fragmented the
      // cache between the grid's own call (selectedTab: "ownerV2"/"guest")
      // and useAllTabCounts's per-category call (selectedTab: "wheels"),
      // making them fire two redundant GETs for the same URL whenever they
      // shared a category, and slowing first paint.
      selectedCategory,
      searchedText,
      selectedFilters,
      userId,
      selectedSort,
    ],
    async ({ pageParam = 1 }) => {
      const response = await API.get(
        `${fullApiUrl}&page_size=20&page=${pageParam}`
      );

      return response.data;
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage.current_page < lastPage.total_pages
          ? lastPage.current_page + 1
          : undefined,
      // ISP-9999 (collab-explore-002): #4839 deduped this query's cache key
      // (dropped `selectedTab`) so the grid's call and useAllTabCounts's
      // per-category call share one entry — but with the default
      // `refetchOnMount: true`, a second observer that subscribes to an
      // *already resolved* entry (e.g. CollectionTabs committing a render
      // pass after the grid) still triggers its own background refetch on
      // mount purely because staleTime is 0, firing the identical GET a
      // second time (MEASURED live on web-stg post-#4839-deploy, 2026-09-01:
      // same GET fired twice ~1.8-2.4s apart, CDP-traced through
      // `Query.onSubscribe -> executeFetch` both times — two genuine mounts,
      // not one dispatch site firing twice).
      //
      // `refetchOnMount: false` suppresses only that already-has-data mount
      // refetch; it does NOT touch the query's first-ever fetch (react-query
      // always fetches when there's no cached data yet, regardless of this
      // flag) and does NOT touch `refetchOnWindowFocus` (a separate default,
      // still true here on purpose — see
      // useInfiniteWheels.focusRefetch.test.jsx, which depends on a focus
      // regain refetching even immediately after mount). Explicit
      // invalidation (`queryClient.invalidateQueries(["fetchWheels"])`,
      // called by every wheel mutation — WheelCardThreeDotMenu.jsx,
      // useReorderGallery.js, FavoriteHeartIcon.jsx, NotificationHandler.js)
      // still forces active observers to refetch regardless of this flag.
      refetchOnMount: false,
      refetchInterval: refetchIntervalMs || false,
      // A backgrounded/hidden tab costs nothing — same convention as
      // MiniDmDock's refetchInterval (Chat.helpers.js LIVE_BADGE_POLL_MS).
      refetchIntervalInBackground: false,
      // ISP-9999 fix-ew-cl3-created-chip-undercounts: `apiUrlMap` builds the
      // request URL via raw template-literal interpolation of `userId`
      // (`user_id=${userId}`), so a caller mounting before its own userId
      // prop hydrates (MEASURED live: the Builder Segment Library pane's
      // Created tab firing with `getLoggedInUser()?.id` still undefined)
      // sent the literal string "undefined" as `user_id` — the Backend then
      // 500'd on it (fixed separately, BE PR#3155). Gating the fetch itself
      // means no caller of this shared hook can ever fire a request with a
      // malformed userId, regardless of why it's momentarily unset; once
      // `userId` resolves, `enabled` flips true and react-query fetches
      // normally.
      enabled: userId != null,
    }
  );
};

// export const useInfiniteExploreWheels = ({
//   searchedText,
//   selectedFilters = [],
//   selectedSort = "most_recent",
// }) => {
//   const baseUrl = "http://localhost:8000/api/v2/wheel/explore/";
//   const searchQuery = searchedText ? `&search=${searchedText}` : "";

//   const filters = selectedFilters.length
//     ? selectedFilters.map((filter) => `${filter}=true`).join("&")
//     : "";

//   const fullApiUrl = `${baseUrl}?page_size=20&${filters}${searchQuery}&${selectedSort}=true`;

//   return useInfiniteQuery(
//     ["fetchExploreWheels", searchedText, selectedFilters, selectedSort],
//     async ({ pageParam = 1 }) => {
//       const response = await API.get(`${fullApiUrl}&page=${pageParam}`);
//       return response.data;
//     },
//     {
//       getNextPageParam: (lastPage) =>
//         lastPage.current_page < lastPage.total_pages
//           ? lastPage.current_page + 1
//           : undefined,
//     }
//   );
// };

export const useInfiniteExploreWheels = ({
  searchedText,
  selectedFilters = [],
  selectedSort = "most_recent",
  selectedTag,
}) => {
  const url = "/api/v2/wheel/explore";
  const params = new URLSearchParams();
  params.append("page_size", "20");
  params.append("page", "1");

  if (searchedText) {
    params.append("search", searchedText);
  }

  if (selectedSort) {
    params.append(selectedSort, "true");
  }

  selectedFilters.forEach((filter) => {
    params.append(filter, "true");
  });

  if (selectedTag) {
    params.append("tags", selectedTag);
  }

  // Note: `page` is dynamically updated inside the fetcher below, so we
  // won't build a fixed URL here. Instead we'll construct the URL after
  // setting the correct `page` value for every request.

  return useInfiniteQuery(
    [
      "fetchExploreWheels",
      searchedText,
      selectedFilters,
      selectedSort,
      selectedTag,
    ],
    async ({ pageParam = 1 }) => {
      // Ensure the correct page is sent with every request
      params.set("page", pageParam);
      const response = await API.get(`${url}/?${params.toString()}`);
      return response.data;
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage.current_page < lastPage.total_pages
          ? lastPage.current_page + 1
          : undefined,
    }
  );
};

// Which documents the authenticated user still needs to (re-)accept. Returns
// { enabled, acceptance_required, pending_documents:[{slug,doc_version,is_referral}] }.
// Backend reports nothing pending while TERMS_ACCEPTANCE_ENABLED is OFF, so the
// blocking gate never activates then. Disabled by default; the gate enables it
// only for an authenticated user with the FE flag on.
export const useGetTermsAcceptanceStatus = (config = {}) => {
  return useQuery(
    [QUERY_KEYS.termsAcceptanceStatus],
    () => API.get(API_PATH.GET_TERMS_ACCEPTANCE_STATUS),
    { ...default_config, enabled: false, ...config }
  );
};

export const useGetTermsAndConditions = (config, from_referrals = false) => {
  return useQuery(
    [QUERY_KEYS.termsAndConditions],
    () => {
      // Build URL with multiple slug parameters to match curl format
      const baseUrl = API_PATH.GET_TERMS_AND_CONDITIONS;
      const params = new URLSearchParams();
      params.append("slugs", "tos");
      params.append("slugs", "privacy_policy");
      if (from_referrals) params.append("slugs", "referral_terms");

      return API.get(`${baseUrl}?${params.toString()}`);
    },
    { ...default_config, ...config }
  );
};

export const useInfiniteExploreCreators = ({
  searchedText,
  selectedFilters = [],
  selectedTag,
}) => {
  const url = "/api/v2/wheel/explore-creators";
  const params = new URLSearchParams();
  params.append("page_size", "20");

  if (searchedText) {
    params.append("search", searchedText);
  }

  selectedFilters.forEach((filter) => {
    params.append(filter, "true");
  });

  if (selectedTag) {
    params.append("tags", selectedTag);
  }

  return useInfiniteQuery(
    ["fetchExploreCreators", searchedText, selectedFilters, selectedTag],
    async ({ pageParam = 1 }) => {
      params.set("page", pageParam);
      const response = await API.get(`${url}/?${params.toString()}`);
      return response.data;
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage.current_page < lastPage.total_pages
          ? lastPage.current_page + 1
          : undefined,
    }
  );
};

const fetchTabCounts = async (search) => {
  const response = await API.get(`/api/v2/wheel/tabs_count/?search=${search}`);
  return response.data?.data || {};
};

export const useWheelTabCounts = (search) => {
  return useQuery(["wheelTabCounts", search], () => fetchTabCounts(search), {
    enabled: !!search, // Only fetch when search text exists
  });
};

export const useGetEmailPermissions = (wheelId, searchKeyword) => {
  return useInfiniteQuery(
    ["emailPermissions", wheelId, searchKeyword],
    async ({ pageParam = 1 }) => {
      const response = await API.get(
        `${API_PATH.GET_WHEEL_EMAIL_PERMISSION(
          wheelId
        )}?search=${searchKeyword}&page=${pageParam}&page_size=20`
      );
      return response.data;
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage.current_page < lastPage.total_pages
          ? lastPage.current_page + 1
          : undefined,
      staleTime: 5 * 60 * 1000, // Data remains fresh for 5 minutes
      cacheTime: 10 * 60 * 1000, // Cache time before garbage collection
      refetchOnWindowFocus: false, // Prevent unnecessary refetching
      enabled: !!wheelId, // Ensure the query runs only if `wheelId`
      onError: (error) => {
        console.error("Failed to fetch email permissions:", error);
      },
    }
  );
};

export const useTopTags = () => {
  return useQuery({
    queryKey: ["top-tags"],
    queryFn: async () => {
      const response = await API.get(`/api/v2/wheel/top-tags/`);
      const data = response.data;
      if (!data.success) {
        throw new Error(data.message || "Failed to fetch tags");
      }
      // Filter out empty tags and duplicates
      // return [...new Set(data.data.map((tag, index) => {
      //   return {
      //     id: index,
      //     name: tag,
      //   };
      // }))];
      return data.data;
    },
  });
};

export const useGetPaymentCards = (config = {}) => {
  const { enabled = true, ...restConfig } = config;

  const queryResult = useQuery(
    [QUERY_KEYS.listAllCards],
    async () => {
      const response = await API.get(API_PATH.LIST_ALL_CARDS);
      return response.data.cards || [];
    },
    {
      ...default_config,
      enabled,
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      ...restConfig,
    }
  );

  // Determine the default card
  const defaultCard =
    queryResult.data && queryResult.data.length > 0
      ? queryResult.data.find((card) => card.is_default) || queryResult.data[0]
      : null;

  // Format the default card in the format expected by PaymentCardView
  const formattedDefaultCard = defaultCard
    ? {
        ...defaultCard,
        cardName:
          defaultCard.brand?.charAt(0).toUpperCase() +
            defaultCard.brand?.slice(1) || "Card",
        last4: defaultCard.last4 || "****",
        expDate: `${defaultCard.expiry_month}/${defaultCard.expiry_year}`,
      }
    : null;

  return {
    ...queryResult,
    defaultCard,
    formattedDefaultCard,
  };
};

export const useGetRefundAmount = (config = {}) => {
  return useQuery(
    [QUERY_KEYS.getRefundAmount],
    async () => {
      const response = await API.get(
        "/payments/change-subscription/refund-amount/"
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(
        response.data.message || "Failed to fetch refund information"
      );
    },
    { ...default_config, ...config }
  );
};

// Add the active subscription query hook
export const useActiveSubscriptionQuery = (config = {}) => {
  return useQuery([QUERY_KEYS.activeSubscription], getCurrentPlan, {
    ...default_config,
    staleTime: 0, // Always consider data stale
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: true, // Always refetch when component mounts
    select: (response) => response?.data?.data,
    ...config,
  });
};

export const useUpcomingSubscriptionChanges = (config = {}) => {
  return useQuery(
    [QUERY_KEYS.upcomingSubscriptionChanges],
    async () => {
      const response = await API.get(
        "/payments/change-subscription/upcoming-changes/"
      );
      if (response.data.success) {
        return response.data.data;
        // return {...response.data.data, has_scheduled_changes: true,
        //     current_subscription: {
        //       name: "ProSuite",
        //       tier: 3,
        //       period_end: "2025-05-30T18:31:46",
        //       amount: 50.0,
        //       currency: "usd",
        //       status: "active"
        //     },
        //     change_type: "cancellation",
        //     change_date: "2025-05-30T18:31:46",
        //     effective_date: "May 30, 2025",
        //     new_package: {
        //       name: "Free Plan",
        //       tier: 0,
        //       short_description: "Basic free access"
        //     },
        //     days_remaining: 29
        // };
      }
      throw new Error(
        response.data.message || "Failed to fetch upcoming subscription changes"
      );
    },
    {
      ...default_config,
      staleTime: 0, // Always consider data stale
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnMount: true, // Always refetch when component mounts
      ...config,
    }
  );
};

// Hook to get all tab counts from useInfiniteWheels data
export const useAllTabCounts = (
  userId,
  searchedText = "",
  selectedFilters = {}
) => {
  // Create individual queries for each category
  const createdWheels = useInfiniteWheels({
    selectedTab: "wheels",
    selectedCategory: "Created",
    searchedText,
    userId,
    selectedFilters: selectedFilters.Created || [],
  });

  const clonedWheels = useInfiniteWheels({
    selectedTab: "wheels",
    selectedCategory: "Cloned",
    searchedText,
    userId,
    selectedFilters: selectedFilters.Cloned || [],
  });

  const unlockedWheels = useInfiniteWheels({
    selectedTab: "wheels",
    selectedCategory: "Unlocked",
    searchedText,
    userId,
    selectedFilters: selectedFilters.Unlocked || [],
  });

  const favouritedWheels = useInfiniteWheels({
    selectedTab: "wheels",
    selectedCategory: "Favourited",
    searchedText,
    userId,
    selectedFilters: selectedFilters.Favourited || [],
  });

  // Extract counts from each category's data
  const tabCounts = {
    created:
      createdWheels.data?.pages && createdWheels.data.pages.length > 0
        ? createdWheels.data.pages[0]?.count || 0
        : 0,
    cloned:
      clonedWheels.data?.pages && clonedWheels.data.pages.length > 0
        ? clonedWheels.data.pages[0]?.count || 0
        : 0,
    unlocked:
      unlockedWheels.data?.pages && unlockedWheels.data.pages.length > 0
        ? unlockedWheels.data.pages[0]?.count || 0
        : 0,
    favorited:
      favouritedWheels.data?.pages && favouritedWheels.data.pages.length > 0
        ? favouritedWheels.data.pages[0]?.count || 0
        : 0,
    // ISP-9999: per-category loading flags so a consumer (CollectionTabs) can
    // tell "still fetching" apart from "resolved to a real 0" — without this,
    // every badge reads a bare 0 for as long as its query is in flight.
    isLoading: {
      created: createdWheels.isLoading,
      cloned: clonedWheels.isLoading,
      unlocked: unlockedWheels.isLoading,
      favorited: favouritedWheels.isLoading,
    },
  };

  return tabCounts;
};

// ── WL-019 Creator Analytics (existing yuga_wheel metrics endpoints) ──────────
// All three reads return the raw payload (res.data) so consumers use `data`
// directly. Keyed per-endpoint; totals/analytics/hall are independent panels.

export const useUserWheelStats = (config = {}) => {
  return useQuery(
    [QUERY_KEYS.useUserWheelStats],
    () => API.get(API_PATH.CREATOR_STATS).then((res) => res.data),
    { ...default_config, ...config }
  );
};

export const useUserWheelAnalytics = (config = {}) => {
  return useQuery(
    [QUERY_KEYS.useUserWheelAnalytics],
    () => API.get(API_PATH.CREATOR_WHEEL_ANALYTICS).then((res) => res.data),
    { ...default_config, ...config }
  );
};

export const useUserWheelHallOfFame = (config = {}) => {
  return useQuery(
    [QUERY_KEYS.useUserWheelHallOfFame],
    () => API.get(API_PATH.CREATOR_HALL_OF_FAME).then((res) => res.data),
    { ...default_config, ...config }
  );
};

// ── U12a Settings storage meter (GET /api/media/total-storage/) ───────────────
// Unwraps the response envelope and returns the STRING payload
// { storage_used, storage_limit, storage_used_percentage, storage_remaining }.
// The live API double-wraps: axios `res.data` is the envelope
// { success, message, status_code, data: { storage_used, … } }, so the fields
// live one level deeper. Envelope shapes vary across this codebase ({ data:{…} }
// vs { data:{ data:{…} } }) — mirror useStorageUsage's tolerant unwrap so a
// shape change can never blank the meter (a raw/flat payload still passes
// through). User-scoped by the authed axios instance. 60s staleTime.
export const useTotalStorage = (config = {}) => {
  return useQuery(
    [QUERY_KEYS.useTotalStorage],
    () =>
      getTotalStorage().then((res) => {
        const body = res?.data;
        return body?.data ?? body ?? {};
      }),
    { staleTime: 60_000, ...default_config, ...config }
  );
};

// ── DL-053 Settings › Notification preferences (GET /api/v1/notification/preferences/)
// Returns the raw axios response; the panel unwraps res.data (or res.data.data
// for the envelope shape). Gated behind the backend NOTIFICATION_PREFERENCES_ENABLED
// (404s while OFF) — the FE flag keeps this hook from mounting at all while OFF.
export const useGetNotificationPreferences = (config = {}) => {
  return useQuery(
    [QUERY_KEYS.notificationPreferences],
    () => API.get(API_PATH.NOTIFICATION_PREFERENCES),
    { ...default_config, ...config }
  );
};

/* --------------------------------------------------------------------------
 * Wheel Comments (v1, whole-wheel). Gated by COMMENTS_ENABLED at the callsite.
 * Paginated newest-first; envelope { count, current_page, total_pages, next,
 * previous, results }. See specs/comments/SCOPE-2026-07-21.md §2g.
 * ------------------------------------------------------------------------ */
export const COMMENTS_PAGE_SIZE = 15;

export const useWheelComments = (wheelId, { enabled = true, ...config } = {}) => {
  return useInfiniteQuery(
    ["wheel-comments", wheelId],
    async ({ pageParam = 1 }) => {
      const response = await API.get(API_PATH.WHEEL_COMMENTS(wheelId), {
        params: { page: pageParam, page_size: COMMENTS_PAGE_SIZE },
      });
      return response.data;
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage?.current_page < lastPage?.total_pages
          ? lastPage.current_page + 1
          : undefined,
      enabled: !!wheelId && enabled,
      refetchOnWindowFocus: false,
      staleTime: 0,
      ...config,
    }
  );
};

// One-level replies for a top-level comment. Disabled until the thread is expanded.
export const useCommentReplies = (commentId, { enabled = false, ...config } = {}) => {
  return useInfiniteQuery(
    ["comment-replies", commentId],
    async ({ pageParam = 1 }) => {
      const response = await API.get(API_PATH.COMMENT_REPLIES(commentId), {
        params: { page: pageParam, page_size: COMMENTS_PAGE_SIZE },
      });
      return response.data;
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage?.current_page < lastPage?.total_pages
          ? lastPage.current_page + 1
          : undefined,
      enabled: !!commentId && enabled,
      refetchOnWindowFocus: false,
      staleTime: 0,
      ...config,
    }
  );
};
